import React from 'react';
import { Panel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PagedEntitiesList from '../../common/components/PagedEntitiesList';
import ui from '../../common/services/ui';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { faAlignJustify, faLink } from '@fortawesome/free-solid-svg-icons';

const renderQuantityCell = (remainingQuantity, row) => {
	return remainingQuantity + (row.refundedQuantity ? ` (${row.refundedQuantity})` : '');
};

const renderChildCell = (child, row, parentId) => {
	if (parentId > 0) {
		return `${row.child.fullname} (${ui.formatShortDate(row.child.dateOfBirth)})`;
	}
	else {
		return <Link to={`/parents/${row.parent.id}/children/${row.child.id}`} > {row.child.fullname} ({ui.formatShortDate(row.child.dateOfBirth)})</Link>;
	}
};

const countUpcomingLessons = (row) => {
	return row.allLessons ? row.allLessons.filter(l => l.isInTheFuture).length : 0;
};

const List = ({ isReadonly, t, parentId, canSendMessage, requireAddress, requirePostcode, listActions, requireChildSchool, onlyEnrolmentData, enableAutoEnrolment, ...rest }) => {
	const columns = [
		{
			header: t('Enrolment_Id'),
			accessor: 'id',
			sortable: true,
			cell: (id, row) => <Link to={`/enrolments/${id}`}> {id} {(row.paymentParentId || row.paymentType.id == 7) && <FontAwesomeIcon icon={row.paymentParentId ? faLink : faAlignJustify} />} </Link>
		}];
	if (!onlyEnrolmentData) {
		if (!parentId || parentId === 0) {
			columns.push(
				{
					header: t('PaymentHistory_Parent'),
					accessor: 'parent.fullname',
					cell: (parent, row) => {
						if (parentId > 0) {
							return row.parent.fullname;
						}
						else {
							return <Link to={`/parents/${row.parent.id}`} > {row.parent.fullname}</Link>;
						}
					},
					sortable: true
				});
		}
		columns.push({
			header: t('PaymentHistory_Child'),
			accessor: 'child.fullname',
			cell: (child, row) => renderChildCell(child, row, parentId),
			sortable: true
		});
		if (requireChildSchool) {
			columns.push({
				header: t('Enrolment_Child_School'),
				accessor: 'child.schoolName',
				sortable: true
			});
		}
		if (requireAddress) {
			columns.push({
				header: t('Parent_Address'),
				accessor: 'parent.address',
				sortable: true
			});
		}
		if (requirePostcode) {
			columns.push({
				header: t('Parent_PostCode'),
				accessor: 'parent.postcode',
				sortable: true
			});
		}
		columns.push({
			header: t('Enrolment_Email'),
			accessor: 'parent.email',
			sortable: true
		});
		columns.push({
			header: t('Enrolment_PhoneNumber'),
			accessor: 'parent.phoneNumber',
			sortable: true
		});
	}
	columns.push({
		header: t('PaymentHistory_Center'),
		accessor: 'center',
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_Term'),
		accessor: 'term.name',
		sortable: true
	});
	columns.push({
		header: t('ReportChildren_Grade'),
		accessor: 'grade.name',
		cell: (gradeName, row) => row.name ? <div>{row.name}<div className='text-muted'>{gradeName}</div></div> : gradeName,
		sortable: true
	});
	columns.push({
		header: t('ReportChildren_WeekDay'),
		accessor: 'dayOfWeek',
		cell: dayOfWeek => dayOfWeek && t(ui.days[dayOfWeek]),
		sortable: true
	});
	columns.push({
		header: t('ReportChildren_StartTime'),
		accessor: 'startTime',
		cell: startTime => ui.formatTime(startTime),
		sortable: true
	});
	columns.push({
		header: t('PaymentHistory_NumberOfLessons'),
		accessor: 'remainingQuantity',
		sortable: true,
		cell: renderQuantityCell
	});
	if (onlyEnrolmentData) {
		columns.push({
			header: t('PaymentHistory_UpcomingLessons'),
			accessor: 'allLessons',
			cell: (allLessons, row) => countUpcomingLessons(row),
			sortable: false
		});
	}
	if (!onlyEnrolmentData) {
		if (enableAutoEnrolment) {
			columns.push({
				header: t('Enrolment_IsAutoEnrolment'),
				accessor: 'isAutoEnrolment',
				cell: isAutoEnrolment => isAutoEnrolment ? t('Common_Yes') : '',
				sortable: false
			});
		}
		columns.push({
			header: t('Enrolment_Type'),
			accessor: 'typeResource',
			cell: (typeResource, row) => t(typeResource) + (row.fullDiscountResource ? t(row.fullDiscountResource) : ""),
			sortable: false
		});
		columns.push({
			header: t('PaymentHistory_PaymentType'),
			accessor: 'paymentType',
			cell: (paymentType) => t(`PaymentTypeEnum_${paymentType.name}`),
			sortable: true
		});
	}
	columns.push({
		header: t('PaymentHistory_Created'),
		accessor: 'created',
		sortable: true,
		cell: (created) => (ui.formatDateTime(created))
	});
	columns.push({
		header: t('Common_Actions'),
		accessor: 'id',
		cell: (id, row) => (<div>
			<Link to={`/enrolments/${id}`} className='btn btn-teal'><FontAwesomeIcon icon={faEye} /> {t('Common_View')}</Link>
		</div>)
	});

	const list = <PagedEntitiesList columns={columns} checkboxSelection={!parentId && canSendMessage} listActions={listActions} {...rest} />;

	return onlyEnrolmentData ? list :
		<Panel>
			<Panel.Body>
				{list}
			</Panel.Body>
		</Panel>;
};

export default withTranslation()(List);